// A firm's brand as design tokens (design v0.9, V1): from its two colors,
// every brand token the apps use, each pair guaranteed to read at 4.5:1. The
// tints and inks are worked out in OKLCH, so a tint of any hue looks equally
// light. This is the only place brand tokens are made (engineering conventions).

export type Oklch = { l: number; c: number; h: number };

// StaffSmith's own brand, for a firm with none and for the operator console (Q154).
export const DEFAULT_BRAND = { primary: "#1F4E6B", accent: "#E0A33A" } as const;
export const INK = "#1F1F1D";
export const WHITE = "#FFFFFF";
export const MIN_CONTRAST = 4.5;

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
export const isHex = (v: unknown): v is string => typeof v === "string" && HEX.test(v.trim());

function rgbOf(hex: string): [number, number, number] {
  let h = hex.trim().replace("#", "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
}
const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function toOklch(hex: string): Oklch {
  const [r, g, b] = rgbOf(hex).map(toLinear) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
}

function linearOf({ l: L, c, h }: Oklch): [number, number, number] {
  const A = c * Math.cos((h * Math.PI) / 180);
  const B = c * Math.sin((h * Math.PI) / 180);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

// Back to a hex color, giving up chroma (never lightness or hue) to stay in sRGB.
export function fromOklch(color: Oklch): string {
  let c = color.c;
  let rgb = linearOf({ ...color, c });
  for (let i = 0; i < 40 && rgb.some((v) => v < -0.0001 || v > 1.0001); i++) {
    c *= 0.93;
    rgb = linearOf({ ...color, c });
  }
  return `#${rgb
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, fromLinear(Math.min(1, Math.max(0, v))))) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase()}`;
}

const luminance = (hex: string) => {
  const [r, g, b] = rgbOf(hex).map(toLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// WCAG contrast ratio, 1 to 21.
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

// White or ink, whichever reads better on the color.
export const textOn = (bg: string) => (contrast(bg, WHITE) >= contrast(bg, INK) ? WHITE : INK);

const shift = (hex: string, dl: number) => {
  const o = toOklch(hex);
  return fromOklch({ ...o, l: Math.min(1, Math.max(0, o.l + dl)) });
};
const at = (hex: string, l: number, chroma = 1) => {
  const o = toOklch(hex);
  return fromOklch({ l, c: o.c * chroma, h: o.h });
};

// A color text can sit on: darkened in OKLCH until white reaches 4.5:1, when
// neither white nor ink does (Q157). Returns it unchanged otherwise.
export function readable(hex: string): string {
  let color = fromOklch(toOklch(hex));
  if (Math.max(contrast(color, WHITE), contrast(color, INK)) >= MIN_CONTRAST) return color;
  for (let i = 0; i < 60 && contrast(color, WHITE) < MIN_CONTRAST; i++) color = shift(color, -0.01);
  return color;
}

// An ink for a tint: the color at the given lightness, darkened until it reads.
function inkFor(hex: string, tint: string, l: number, chroma = 0.7) {
  let ink = at(hex, l, chroma);
  for (let i = 0; i < 40 && contrast(ink, tint) < MIN_CONTRAST; i++) ink = shift(ink, -0.02);
  return ink;
}

export type BrandTokens = {
  light: Record<string, string>;
  dark: Record<string, string>;
  // Colors changed so text can sit on them, for the Branding page's note.
  adjusted: { primary?: { from: string; to: string }; accent?: { from: string; to: string } };
};

export function deriveBrand(primary?: string | null, accent?: string | null): BrandTokens {
  const p = isHex(primary) ? fromOklch(toOklch(primary)) : DEFAULT_BRAND.primary;
  const a = isHex(accent) ? fromOklch(toOklch(accent)) : DEFAULT_BRAND.accent;
  const brand = readable(p);
  const acc = readable(a);
  const brandTint = at(brand, 0.93, 0.35);
  const accentTint = at(acc, 0.945, 0.36);
  const light = {
    "--brand": brand,
    "--brand-strong": shift(brand, -0.06),
    "--brand-tint": brandTint,
    "--brand-tint-ink": inkFor(brand, brandTint, 0.37, 0.8),
    "--brand-stripe": shift(brand, 0.18),
    "--on-brand": textOn(brand),
    "--accent": acc,
    "--accent-strong": shift(acc, -0.06),
    "--on-accent": textOn(acc),
    "--accent-tint": accentTint,
    "--accent-tint-ink": inkFor(acc, accentTint, 0.43, 0.63),
  };
  // Dark mode: brand surfaces darker; tints become the color at low lightness
  // under light inks.
  const darkBrand = shift(brand, -0.08);
  const darkTint = at(brand, 0.3, 0.5);
  const darkAccentTint = at(acc, 0.32, 0.5);
  const lightInk = (hex: string, bg: string, l: number) => {
    let ink = at(hex, l, 0.6);
    for (let i = 0; i < 30 && contrast(ink, bg) < MIN_CONTRAST; i++) ink = shift(ink, 0.02);
    return ink;
  };
  const dark = {
    "--brand": darkBrand,
    "--brand-strong": shift(darkBrand, -0.06),
    "--brand-tint": darkTint,
    "--brand-tint-ink": lightInk(brand, darkTint, 0.85),
    "--brand-stripe": shift(darkBrand, 0.14),
    "--on-brand": textOn(darkBrand),
    "--accent": acc,
    "--accent-strong": shift(acc, -0.06),
    "--on-accent": textOn(acc),
    "--accent-tint": darkAccentTint,
    "--accent-tint-ink": lightInk(acc, darkAccentTint, 0.88),
  };
  const adjusted: BrandTokens["adjusted"] = {};
  if (brand !== p) adjusted.primary = { from: p, to: brand };
  if (acc !== a) adjusted.accent = { from: a, to: acc };
  return { light, dark, adjusted };
}

// The tokens as CSS, for a <style> element: light by default, dark under the
// person's setting or their system's.
export function brandCss(tokens: BrandTokens): string {
  const decl = (t: Record<string, string>) =>
    Object.entries(t)
      .map(([k, v]) => `${k}: ${v};`)
      .join(" ");
  return [
    `:root { ${decl(tokens.light)} }`,
    `@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${decl(tokens.dark)} } }`,
    `:root[data-theme="dark"] { ${decl(tokens.dark)} }`,
  ].join("\n");
}
